<script setup>

import {computed, onMounted, ref} from "vue";

const currentCardTab = ref('coach');
const cardTransitioning = ref(false);
const currentTestimonial = ref(0)
const testimonialTransitioning = ref(false)
const transitionsActive = ref(true)

const props = defineProps({
    uuid: String,
    webinarId: {
        type: [String, Number],
        default: null,
    },
    webinarDetails: {
        type: Object,
        default: null,
    },
});

const registerLink = computed(() => {
    const baseUrl = route('expert.register.shortcut', props.uuid);
    const params = new URLSearchParams({ utm_source: 'info_page' });

    if (props.webinarId) {
        params.set('webinar', props.webinarId);
    }

    return `${baseUrl}?${params.toString()}`;
});

const webinarInfo = computed(() => {
    if (! props.webinarDetails) {
        return null;
    }

    return {
        title: props.webinarDetails.title,
        description: props.webinarDetails.description,
        schedule: props.webinarDetails.start_iso
            ? new Intl.DateTimeFormat(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                  timeZoneName: 'short',
              }).format(new Date(props.webinarDetails.start_iso))
            : null,
        language: props.webinarDetails.language
            ? String(props.webinarDetails.language).toUpperCase()
            : null,
        duration: props.webinarDetails.duration_minutes
            ? `${props.webinarDetails.duration_minutes} minutes`
            : null,
    };
});

const cardTabs = {
    coach: {
        title: 'Coach',
        slug: 'coach',
        headline: 'Everything you need as a coach',
        byline: 'Get flow in your coaching business. Packed with features to generate new revenue, acquire more customers, and save time.',
        image: 'https://myflow.fra1.digitaloceanspaces.com/prereg/marie.png',
        boxes: [
            'Coaching',
            'Scheduling',
            'Payments',
            'CRM',
            'Web Pages',
            'Lead Hunter AI',
            'Newsletter',
            'Quotes',
            'Agreements',
        ],
    },
    consultant: {
        title: 'Consultant',
        slug: 'consultant',
        headline: 'Everything you need as a consultant',
        byline: 'Get flow in your consulting business. Packed with features to generate new revenue, acquire more customers, and save time.',
        image: 'https://myflow.fra1.digitaloceanspaces.com/prereg/thibbe.png',
        boxes: [
            'Time Tracking',
            'Quotes',
            'Agreements',
            'Website',
            'Landing Pages',
            'Lead Hunter AI',
            'Payments',
            'CRM',
            'Newsletter',
        ],
    },
    speaker: {
        title: 'Speaker',
        slug: 'speaker',
        headline: 'Everything you need as a speaker',
        byline: 'Get flow in your speaker business. Packed with features to generate new revenue, acquire more customers, and save time.',
        image: 'https://myflow.fra1.digitaloceanspaces.com/prereg/charbel.png',
        boxes: [
            'Payments',
            'Website',
            'Landing Pages',
            'Agreements',
            'Quotes',
            'Courses',
            'Newsletter',
            'Lead Hunter AI',
            'Automations',
        ],
    },
    educator: {
        title: 'Creator',
        slug: 'educator',
        headline: 'Everything you need as an creator',
        byline: 'Get flow in your creator business. Packed with features to generate new revenue, acquire more customers, and save time.',
        image: 'https://myflow.fra1.digitaloceanspaces.com/prereg/jonathan.png',
        boxes: [
            'Courses',
            'Subscriptions',
            'Newsletters',
            'Payment',
            'Automations',
            'Funnels',
            'CRM',
            'Landing Pages',
            'Website',
        ],
    },
}

const testimonials = [
    {
        body: 'I love how myflow simplifies my everyday life as an entrepreneur. It\'s is almost too good to be true. myflow really helps me and my clients to streamline our business.',
        author: 'Mari Höglund, Coach & Growth Assistant',
        image: 'https://myflow.fra1.digitaloceanspaces.com/prereg/marie.png'
    },
    {
        body: 'I was tired of patching together dozens of software applications. Now, a single platform manages my business, covering everything from CRM to marketing & sales.',
        author: 'Thibaud Hartwig, Management Consultant',
        image: 'https://myflow.fra1.digitaloceanspaces.com/prereg/thibbe.png'
    },
    {
        body: 'I love how easy it is for me to share my passion and knowledge to thousands of people all over the world. myflow really helped me to kick-start my digital journey.',
        author: 'Charbel Gabro, Keynote Speaker',
        image: 'https://myflow.fra1.digitaloceanspaces.com/prereg/charbel.png'
    },
    {
        body: 'I train coaches & speakers daily. I can truly see the difference myflow makes for them - both new and seasoned professionals progress towards their goals.',
        author: 'Jonathan Ljungqvist, Coach Educator',
        image: 'https://myflow.fra1.digitaloceanspaces.com/prereg/jonathan.png'
    },
]

const manuallySetCardTab = (tab) => {
    const currentTabs = {
        coach: {
            tab: 'consultant',
            testimonial: 0,
        },
        consultant: {
            tab: 'speaker',
            testimonial: 1,
        },
        speaker: {
            tab: 'educator',
            testimonial: 2,
        },
        educator: {
            tab: 'coach',
            testimonial: 3,
        },
    }

    transitionsActive.value = false
    currentCardTab.value = tab;
    currentTestimonial.value = currentTabs[tab].testimonial;
}

const setCardTab = (tab) => {
    cardTransitioning.value = true;
    setTimeout(function(){
        currentCardTab.value = tab;

        setTimeout(function(){
            cardTransitioning.value = false;
        }, 150)
    }, 300)
};

const setTestimonial = (index) => {
    testimonialTransitioning.value = true;
    setTimeout(function () {
        currentTestimonial.value = index;

        setTimeout(function () {
            testimonialTransitioning.value = false;
        }, 150)
    }, 300)
}

const setNextTab = () => {
    const nextTabs = {
        coach: {
            tab: 'consultant',
            testimonial: 1,
        },
        consultant: {
            tab: 'speaker',
            testimonial: 2,
        },
        speaker: {
            tab: 'educator',
            testimonial: 3,
        },
        educator: {
            tab: 'coach',
            testimonial: 0,
        },
    }

    setCardTab(nextTabs[currentCardTab.value].tab);
    setTestimonial(nextTabs[currentCardTab.value].testimonial);
}

onMounted(() => {
    setInterval(function() {
        if (transitionsActive.value === false) {
            return;
        }
        cardTransitioning.value = true;
        setTimeout(function () {
            setNextTab()
            setTimeout(function () {
                cardTransitioning.value = false;
            }, 150)
        }, 300)
    }, 8000)

    setInterval(function() {
        if (transitionsActive.value === false) {
            return;
        }
        testimonialTransitioning.value = true;
        setTimeout(function () {
            currentTestimonial.value++;

            if (currentTestimonial.value >= testimonials.length) {
                currentTestimonial.value = 0;
            }

            setTimeout(function () {
                testimonialTransitioning.value = false;
            }, 150)
        }, 300)
    }, 8000)
})

</script>
<template>
    <div class="bg-white">
        <div class="relative z-10 w-full py-4 text-sm text-center bg-myflowPurple-light ">
            You have been invited to myflow
        </div>


        <div class="min-h-screen pt-20 -mt-8 onboarding-faded-bg">
            <h1 class="pt-12 mb-8 text-3xl font-semibold text-center md:text-5xl" style="line-height: 1.2em;">
                Boost your<br>
                <span class="text-myflowPurple">business flow</span>
            </h1>

            <div
                v-if="webinarInfo"
                class="mx-4 p-6 mb-8 text-left border shadow-sm rounded-xl border-myflowPurple-200 bg-myflowPurple-50 my-8 max-w-3xl mx-auto"
            >
                <p class="mb-1 text-xs font-semibold tracking-wide uppercase text-myflowPurple-700">
                    Upcoming Webinar
                </p>
                <h1 class="text-2xl font-bold md:text-3xl">{{ webinarInfo.title }}</h1>
                <div v-html="
                        webinarInfo.description.replace(
                            /\n/g,
                            '<br>'
                        )
                    " class="mt-3 text-sm leading-relaxed" v-if="webinarInfo.description"></div>
                <div class="flex flex-wrap items-center gap-4 mt-4 text-xs font-medium">
                        <span v-if="webinarInfo.schedule">
                            <i class="mr-1 fa-regular fa-calendar"></i>{{ webinarInfo.schedule }}
                        </span>
                    <span v-if="webinarInfo.duration">
                            <i class="mr-1 fa-regular fa-hourglass-half"></i>{{ webinarInfo.duration }}
                        </span>
                    <span v-if="webinarInfo.language">
                            <i class="mr-1 fa-regular fa-globe"></i>{{ webinarInfo.language }}
                        </span>
                </div>
            </div>

            <div v-if="webinarInfo" class="flex flex-col items-center">
                <a :href="registerLink">
                    <button
                        class="px-10 py-3 mb-2 text-white rounded-lg bg-myflowPurple"
                    >
                        Register for free
                    </button>
                </a>
                <p class="text-xs">Basic is free forever</p>
            </div>

            <div class="w-full max-w-5xl p-4 mx-auto">
                <div class="w-full max-w-2xl mx-auto mb-12 lg:max-w-3xl">
                    <div class="p-4 rounded-4xl shadow-card">
                        <div class="lg:grid lg:grid-cols-3 p-6 card-inner rounded-3xl">
                            <div class="col-span-3 w-full flex justify-between mb-6">
                                <h2 class="font-medium text-white text-2xl opacity-80 -mt-2.5 md:mt-0">
                                    Smart tools to simplify and<br>
                                    grow your business
                                </h2>
                                <div class="text-white">
                                    <img src="https://myflow.fra1.digitaloceanspaces.com/myflow-brand/logo-white.svg" class="w-24" alt="myflow logo" />
                                </div>
                            </div>
                            <div class="col-span-2">
                                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div
                                        v-for="(item, i) in ['Web Pages', 'Landing Pages', 'Newsletters', 'Forms', 'LeadHunter', 'Automations', 'Contacts', 'Agreements', 'Time Tracking', 'Media Library', 'User Area', 'Coaching', 'Courses', 'Community', 'Quotes', 'Payments']"
                                        class="border-2 border-myflowPurple rounded-lg px-2 flex items-center justify-center text-xs font-medium py-3 text-white whitespace-nowrap"
                                    >
                                        <span>{{ item }}</span>
                                    </div>
                                </div>
                                <div class="mt-8 flex flex-col md:flex-row justify-center items-center space-x-4 space-y-2 md:space-y-0">
                                    <a :href="registerLink">
                                        <button
                                            class="px-10 py-3 text-white rounded-lg bg-myflowPurple text-lg"
                                        >
                                            Register for free
                                        </button>
                                    </a>
                                    <span class="text-white/80 italic">No credit card required</span>
                                </div>
                            </div>
                            <div class="flex items-end justify-center lg:justify-start">
                                <div
                                    v-for="(testimonial, key ) in testimonials"
                                    class="flex items-end col-span-2 transition-all max-w-xs mt-6 md:mt-16 lg:mt-0 -mb-2 lg:mb-0"
                                    :class="cardTransitioning ? 'opacity-0 duration-300' : 'opacity-100 duration-1000'"
                                >
                                    <img
                                        v-if="currentTestimonial === key"
                                        :src="testimonial.image"
                                        class="-mb-4 origin-bottom transform scale-110 md:-mb-4 md:translate-x-10 md:scale-125 lg:-mb-6"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full max-w-2xl mx-auto mb-12 lg:max-w-3xl">
                    <div class="p-2 rounded-4xl shadow-card">
                        <div
                            v-for="(testimonial, key) in testimonials"
                            class="overflow-hidden transition-all duration-500"
                        >
                            <div v-if="currentTestimonial === key">
                                <div
                                    class="flex flex-col-reverse p-4 m-2 space-x-4 bg-gray-100 rounded-3xl md:flex-row md:p-6 lg:p-8"
                                    :class="testimonialTransitioning ? 'opacity-0 duration-300' : 'opacity-100 duration-1000'"
                                >
                                    <div class="flex items-center justify-center mt-4 space-x-1 w-80 md:mt-0 md:justify-start">
                                        <img
                                            v-for="i in 5"
                                            src="https://myflow.fra1.digitaloceanspaces.com/prereg/Icon%20fa-solid-star.png"
                                            class="w-6 h-6"
                                        />
                                    </div>
                                    <div>
                                        <div class="md:w-3/4">
                                            <p class="mb-1">{{ testimonial.body }}</p>
                                            <div class="font-semibold">{{ testimonial.author }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="my-8 flex justify-center items-center space-x-4">
                    <a :href="registerLink">
                        <button
                            class="px-10 py-3 text-white rounded-lg bg-myflowPurple text-lg"
                        >
                            Register for free
                        </button>
                    </a>
                </div>

                <div class="py-4">
                    <video-player
                        class="max-w-xl mx-auto mb-8"
                        mp4="https://myflow.fra1.digitaloceanspaces.com/prereg/myflow-onboarding-1.mp4"
                        :autoplay="false"
                    ></video-player>
                </div>

                <div class="flex flex-col items-center">
                    <a :href="registerLink">
                        <button
                            class="px-10 py-3 mb-2 text-white rounded-lg bg-myflowPurple"
                        >
                            Register for free
                        </button>
                    </a>
                    <p class="text-xs">Basic is free forever</p>
                </div>
            </div>
        </div>
        <div class="">
            <div class="w-full max-w-5xl p-4 mx-auto">
                <div class="pb-64">
                    <div class="w-full max-w-md p-4 mx-auto">
                        <div class="mb-8 text-4xl font-semibold text-center">
                            <p>The <span class="text-myflowPurple">all-in-one</span> platform for solopreneurs and small teams</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style>
.shadow-tab {
    box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.25);
}

.shadow-card {
    box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.15);
}

.card-inner {
    background-image: url('https://myflow.fra1.digitaloceanspaces.com/prereg/background.png');
    background-size: cover;
    background-position: bottom center;
}

.onboarding-faded-bg {
    background-image: url(https://myflow.fra1.digitaloceanspaces.com/prereg/faded-bg.png);
    background-size: contain;
    background-position: top center;
    background-repeat: no-repeat;
}
</style>
